import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { markAssessmentStarted } from '../components/EmailCapturePopup';
import { ASSESSMENT_SECTIONS, fireTrackingEvent } from '../components/intake/intakeData';
import IntakeIntro from '../components/intake/IntakeIntro';
import IntakeQuestions from '../components/intake/IntakeQuestions';
import IntakeContactForm from '../components/intake/IntakeContactForm';
import IntakeResults from '../components/intake/IntakeResults';
import IntakeExitPopup from '../components/intake/IntakeExitPopup';

const CALENDLY_URL = 'https://calendly.com/glowhealthsupport-info/glow-health-initial-consultation';
const STORAGE_KEY = 'glow_intake_progress_v1';
const STORAGE_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

const loadProgress = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.savedAt) return null;
    if (Date.now() - data.savedAt > STORAGE_TTL_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const saveProgress = (progress) => {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...progress, savedAt: Date.now() })
    );
  } catch { /* quota or private mode — silent */ }
};

const clearProgress = () => {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
};

const Intake = () => {
  const navigate = useNavigate();
  // Hydrate from sessionStorage so visitors who get interrupted resume mid-quiz
  const saved = typeof window !== 'undefined' ? loadProgress() : null;

  // Flow: intro → questions → transition → results
  const [step, setStep] = useState(saved && saved.step === 'questions' ? 'questions' : 'intro');
  const [section, setSection] = useState(saved?.section ?? 0);
  const [question, setQuestion] = useState(saved?.question ?? 0);
  const [answers, setAnswers] = useState(saved?.answers ?? {});
  const [hasResumedSession] = useState(!!saved && Object.keys(saved.answers || {}).length > 0);
  const [contact, setContact] = useState({ firstName: '', email: '' });
  const [assessmentId, setAssessmentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Persist progress on every meaningful change
  useEffect(() => {
    if (step === 'questions') {
      saveProgress({ step, section, question, answers });
    }
    if (step === 'results') {
      // User finished — clear the stored draft
      clearProgress();
    }
  }, [step, section, question, answers]);

  // Mount tracking
  useEffect(() => {
    fireTrackingEvent('PageView');
    markAssessmentStarted();
  }, []);

  // Step-based ViewContent
  useEffect(() => {
    if (step === 'questions') {
      fireTrackingEvent('ViewContent', {
        content_name: 'Assessment Questions',
        content_category: 'Hormone Assessment',
      });
    } else if (step === 'results') {
      fireTrackingEvent('ViewContent', {
        content_name: 'Assessment Results',
        content_category: 'Hormone Assessment',
      });
    }
  }, [step]);

  // Answer handler with mutex logic for "none" / "sleep_fine"
  const handleAnswer = (questionId, value, isMulti) => {
    setAnswers((prev) => {
      if (!isMulti) return { ...prev, [questionId]: value };

      const current = prev[questionId] || [];
      if (value === 'none' || value === 'sleep_fine') {
        return { ...prev, [questionId]: [value] };
      }
      if (current.includes('none') || current.includes('sleep_fine')) {
        return { ...prev, [questionId]: [value] };
      }
      if (current.includes(value)) {
        return { ...prev, [questionId]: current.filter((v) => v !== value) };
      }
      return { ...prev, [questionId]: [...current, value] };
    });
  };

  const handleNext = () => {
    const sectionData = ASSESSMENT_SECTIONS[section];
    if (question < sectionData.questions.length - 1) {
      setQuestion((q) => q + 1);
    } else if (section < ASSESSMENT_SECTIONS.length - 1) {
      setSection((s) => s + 1);
      setQuestion(0);
    } else {
      fireTrackingEvent('ViewContent', {
        content_name: 'Assessment Complete - Redirect to Form',
        content_category: 'Hormone Assessment',
      });
      navigate('/intake-complete', { state: { answers } });
    }
  };

  const handleBack = () => {
    if (question > 0) {
      setQuestion((q) => q - 1);
    } else if (section > 0) {
      const prev = section - 1;
      setSection(prev);
      setQuestion(ASSESSMENT_SECTIONS[prev].questions.length - 1);
    } else {
      setStep('intro');
    }
  };

  // Submit contact info → save to backend → show results
  const handleContactSubmit = async ({ firstName, email, phone, smsConsent, marketingConsent }) => {
    setSubmitting(true);
    setError(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const consentTimestamp = new Date().toISOString();
      const sourceUrl = window.location.href;

      const res = await fetch(`${API}/api/assessments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName,
          phone,
          sms_consent: smsConsent,
          marketing_consent: marketingConsent,
          consent_timestamp: consentTimestamp,
          source_url: sourceUrl,
          answers,
          completedAt: consentTimestamp,
          source: 'intake_assessment',
        }),
      });

      if (!res.ok) throw new Error('Failed to submit assessment');
      const data = await res.json();
      setAssessmentId(data.id);
      setContact({ firstName, email });

      fireTrackingEvent('AssessmentCompleted', {
        assessment_id: data.id,
        content_name: 'Hormone Health Assessment',
        content_category: 'Assessment',
        first_name: firstName,
        email,
        phone,
      });
      fireTrackingEvent('Lead', {
        content_name: 'Hormone Assessment',
        content_category: 'Assessment',
        value: email,
        first_name: firstName,
        phone,
      });
      fireTrackingEvent('CompleteRegistration', {
        content_name: 'Hormone Assessment Complete',
        status: 'complete',
      });

      sessionStorage.setItem('glow_assessment_completed', 'true');
      setStep('results');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleCall = async () => {
    fireTrackingEvent('EligibilityCTA_Clicked', {
      source: 'post_assessment',
      assessment_id: assessmentId,
    });
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      if (assessmentId) {
        await fetch(`${API}/api/assessments/${assessmentId}/call-scheduled`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      } else if (contact.email) {
        await fetch(`${API}/api/assessments/call-scheduled-by-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: contact.email }),
        });
      }
    } catch (err) {
      console.error('call scheduled failed', err);
    }
    fireTrackingEvent('CalendlyViewed', {
      source: 'post_assessment',
      assessment_id: assessmentId,
    });
    window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
  };

  const handleStartMembership = async () => {
    fireTrackingEvent('InitiateCheckout', {
      source: 'post_assessment',
      assessment_id: assessmentId,
      value: 99.0,
      currency: 'USD',
    });
    fireTrackingEvent('StripeCheckoutViewed', {
      source: 'post_assessment',
      assessment_id: assessmentId,
      value: 99.0,
    });
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/payments/direct-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin,
          email: contact.email,
        }),
      });
      if (!res.ok) throw new Error('Failed to create checkout session');
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Unable to process. Please try again or contact support.');
    }
  };

  return (
    <div className="page-assessment">
      <div className="assessment-container">
        {step === 'intro' && (
          <IntakeIntro
            onBegin={() => setStep('questions')}
            onResume={hasResumedSession ? () => setStep('questions') : null}
            onStartOver={hasResumedSession ? () => {
              clearProgress();
              setSection(0);
              setQuestion(0);
              setAnswers({});
            } : null}
          />
        )}
        <IntakeExitPopup
          active={step === 'questions'}
          currentSection={section}
          totalSections={ASSESSMENT_SECTIONS.length}
        />
        {step === 'questions' && (
          <IntakeQuestions
            sections={ASSESSMENT_SECTIONS}
            currentSection={section}
            currentQuestion={question}
            answers={answers}
            onAnswer={handleAnswer}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}
        {step === 'transition' && (
          <IntakeContactForm
            onSubmit={handleContactSubmit}
            isSubmitting={submitting}
            error={error}
          />
        )}
        {step === 'results' && (
          <IntakeResults
            firstName={contact.firstName}
            email={contact.email}
            answers={answers}
            assessmentId={assessmentId}
            onScheduleCall={handleScheduleCall}
            onStartMembership={handleStartMembership}
            onReturnHome={() => navigate('/')}
          />
        )}
      </div>
    </div>
  );
};

export default Intake;
